import * as yup from 'yup';

import {AGE_GROUPS} from '@/constants';

import {TFunction} from 'i18next';
import {locationShape} from '@/schemas/sub-schemas';
import {addDays} from 'date-fns';

export const TITLE_MAX_LENGTH = 50;
const TITLE_MIN_LENGTH = 2;
export const DESC_MAX_LENGTH = 1000;

const EventValidationSchema = (
  t: TFunction,
  currentParticipants: number | null,
  trainerInSjOne = false,
  bruttoPrice?: number,
  onDemand?: boolean
) =>
  yup.object({
    title: yup
      .string()
      .min(
        TITLE_MIN_LENGTH,
        t('validation:min_length', {
          name: t('the_title'),
          min: TITLE_MIN_LENGTH
        }) || undefined
      )
      .max(
        TITLE_MAX_LENGTH,
        t('validation:max_length', {
          name: t('the_title'),
          max: TITLE_MAX_LENGTH
        }) || undefined
      )
      .required(
        t('validation:is_required', {
          name: t('the_title')
        }) || undefined
      ),
    description: yup
      .string()
      .max(
        DESC_MAX_LENGTH,
        t('validation:max_length', {
          name: t('description'),
          max: DESC_MAX_LENGTH
        }) || undefined
      )
      .required(
        t('validation:is_required', {
          name: t('description')
        }) || undefined
      ),

    location: onDemand
      ? locationShape({t, name: t('locationName')})
          .nullable()
          .notRequired()
      : locationShape({t, name: t('locationName')}).when('online', {
          is: true,
          then: (schema) => schema.nullable('test'),
          otherwise: (schema) =>
            schema.required(
              t('validation_required', {fieldName: t('location')}) +
                ' ' +
                t('for_non_online_event')
            )
        }),
    online: onDemand
      ? yup.boolean().nullable().notRequired()
      : yup
          .boolean()
          .required(t('validation_required', {fieldName: t('online')})),

    maxParticipants: onDemand
      ? yup.mixed().nullable().notRequired()
      : yup
          .mixed()
          .nullable()
          .test(
            'is-number-or-null',
            t('validation:invalid_participants'),
            (value) => {
              return value === null || typeof value === 'number';
            }
          )
          .test(
            'less-than-current',
            t('validation:max_less_than_current'),
            (value) => {
              if (currentParticipants !== null) {
                return (
                  value === null ||
                  (typeof value === 'number' && value >= currentParticipants)
                );
              }
              return true;
            }
          ),

    date: onDemand
      ? yup.date().nullable().notRequired()
      : yup
          .date()
          .required(
            t('validation:is_required', {
              name: t('date')
            }) || undefined
          )
          .typeError(t('validation:date_type'))
          .test(
            'not-past-date',
            t('validation:date_in_past'),
            function (value) {
              const selectedDate = new Date(value);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const selectedTime = this.parent.time;

              if (
                selectedDate.getTime() === today.getTime() &&
                selectedTime < new Date().getHours()
              ) {
                return false;
              }

              if (selectedDate < today) {
                return false;
              }

              return true;
            }
          ),

    time: onDemand
      ? yup.date().nullable().notRequired()
      : yup
          .date()
          .required(
            t('validation:is_required', {
              name: t('time')
            }) || undefined
          )
          .test(
            'not-past-time',
            t('validation:time_in_past'),
            function (value) {
              const {date} = this.parent;
              const dateTime = new Date(date);
              dateTime.setHours(value.getHours());
              dateTime.setMinutes(value.getMinutes());
              return dateTime >= new Date();
            }
          ),

    sports: yup.lazy((value) =>
      Array.isArray(value)
        ? yup
            .array()
            .of(
              yup
                .string()
                .required(
                  t('validation:is_required', {name: t('sport_discipline')})
                )
            )
            .required(
              t('validation:is_required', {name: t('sport_discipline')})
            )
        : yup
            .string()
            .required(
              t('validation:is_required', {name: t('sport_discipline')})
            )
    ),

    accessType: onDemand
      ? yup.string().nullable().notRequired()
      : yup.string().required().oneOf(['PUBLIC', 'PRIVATE']),

    ageGroups: yup
      .array()
      .of(yup.string().oneOf(AGE_GROUPS.map((group) => group.value)))
      .required(
        t('validation:is_required', {
          name: t('ageGroups')
        }) || undefined
      ),
    recurring: yup.array().nullable(),
    end: onDemand
      ? yup.date().nullable().notRequired()
      : yup
          .date()
          .transform((value, originalValue) => {
            if (typeof originalValue === 'string') {
              return new Date(originalValue);
            }
            return value;
          })
          .required(
            t('validation:is_required', {
              name: t('end_date')
            }) || undefined
          )
          .when('recurring', (recurring, schema) => {
            if (recurring && recurring[0] && recurring[0].length > 0) {
              return schema
                .required(
                  t('validation:is_required', {
                    name: t('end_date')
                  }) || undefined
                )
                .test(
                  'is-seven-days-after',
                  t('validation:end_date_must_be_after_start_date', {
                    days: 7
                  }) || '',
                  function (value) {
                    const startDate = this.resolve(yup.ref('date')) as Date;
                    return value.getTime() >= addDays(startDate, 7).getTime();
                  }
                );
            }
            return schema.test(
              'is-same-or-later',
              t('validation:end_date_cannot_be_before_start_date') || '',
              function (value) {
                const startDate = this.resolve(yup.ref('date')) as Date;
                if (!startDate) return true;
                return value.getTime() >= startDate.getTime();
              }
            );
          }),
    endTime: onDemand
      ? yup.date().nullable().notRequired()
      : yup
          .date()
          .required(
            t('validation:is_required', {
              name: t('end_time')
            }) || undefined
          )
          .test(
            'is-later',
            t('validation:end_time_cannot_be_before_start_time') || '',
            function (value) {
              const {date, end, time} = this.parent;

              if (!date || !end || !time || !value) return true;

              const startDate = new Date(date);
              const endDate = new Date(end);

              if (startDate.toDateString() === endDate.toDateString()) {
                const startTime = new Date(time);
                const endTime = new Date(value);

                return endTime.getTime() > startTime.getTime();
              }

              return true;
            }
          ),
    isPaid: yup.boolean(),
    price: yup.number().when('isPaid', {
      is: true,
      then: (schema) =>
        schema
          .required(
            t('validation:is_required', {
              name: t('price')
            }) || undefined
          )
          .test(
            'brutto-price-min',
            t('validation:min_value', {name: t('brutto_price'), min: 0.5}) ??
              '',
            () => (bruttoPrice !== undefined ? bruttoPrice >= 0.5 : true)
          ),
      otherwise: (schema) => schema.nullable().notRequired()
    }),
    imprintLink: yup
      .string()
      .url(t('validation:url_invalid'))
      .when('isPaid', {
        is: true,
        then: (schema) =>
          schema.required(
            t('validation_required', {
              fieldName: t('imprint_link')
            })
          ),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
    termsOfUseLink: yup
      .string()
      .url(t('validation:url_invalid'))
      .when('isPaid', {
        is: true,
        then: (schema) =>
          schema.required(
            t('validation_required', {
              fieldName: t('terms_of_participation_link')
            })
          ),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
    cancellationPolicyLink: yup
      .string()
      .url(t('validation:url_invalid'))
      .notRequired(),

    company: trainerInSjOne
      ? yup.string().required(
          t('validation:is_required', {
            name: t('company')
          }) || undefined
        )
      : yup.string().notRequired(),

    onDemandLink: onDemand
      ? yup
          .string()
          .url(t('validation:url_invalid'))
          .required(
            t('validation:is_required', {
              name: t('on_demand_link')
            }) || undefined
          )
      : yup.string().notRequired().nullable()
  });

export type EventSchemaData = yup.InferType<
  ReturnType<typeof EventValidationSchema>
>;

export default EventValidationSchema;
